<!--

	Die Componente dient als transparent Wrapper für Select Elemente

	Props:

		value		String	Wird als value des Selects verwendet
		label		String	Erste Zeile im <select> z.B. "Bitte wählen ..."
		options		Array	Werden als <option> verwendet
		chunkSize	Integer	Wenn es selects mit sehr vielen options gibt, kann hier bestimmt werden wieviele options sofort gerendert werden sollen. die anderen werden lazy gerendert.


	Markup:

		<BaseMultiSelect
			:value="'heiko'"
			:label="'Choose ...'"
			:chunkSize="100"
			❌:options="[
				{ label : 'Mario',  value : "mario", selected : true },
				{ label : 'Heiko',  value : "heiko", selected : false },
				{ label : 'Silvio', value : "silvio", selected : false },
			]"
			@change="valueChanged( $event )"
		></BaseMultiSelect>

	Changelog
		2021-07-15	improvement: added chunkSize prop for huge options
		2021-07-15	improvement: add disabled prop and styles
		2020-09-02	refactored, added multiple
		2020-08-07	removed label
		2019-06-13	init

-->

<template>
	<div class="BaseMultiSelect" :class="elmClasses">
		<div class="BaseMultiSelect__inner">
			<div  class="BaseMultiSelect__filterBar" v-if="showFilterBar">
				<div class="BaseMultiSelect__filterBarCountSelected">
					{{countSelectedOptions}}
				</div>
				<input
					class="BaseMultiSelect__filterBarInput"
					placeholder="Filter"
					v-model="filterModel"
					@keydown.esc="$event.target.blur()" />
				<div class="BaseMultiSelect__filterBarCountResults">
					{{countFilteredOptions}} / {{countInternalOptions}}
				</div>
			</div>
			<div  class="BaseMultiSelect__optionsList" ref="optionsList">
				<!--
					internalOptionsChunks
				-->
				<pre class="sticky" name="countChunks">{{countChunks}} • {{isVisibleLoadNextChunkTriggerElm}}</pre>

				<div class="BaseMultiSelect__optionsChunk" v-for="(chunk, chunkIndex) in internalOptionsChunks" :key="'chunk' + chunkIndex">
					<!--
						<strong>
							chunk {{chunkIndex}} • {{chunk.length}} • {{ visibleChunkIndexes.includes( chunkIndex ) }}
							<a 	class="BaseMultiSelect__optionsChunkLoadButton"
								v-if="!visibleChunkIndexes.includes( chunkIndex )"
								role="button"
								@click="doRenderOptionsChunk( chunkIndex )"
							>Load more entries ...</a>
						</strong>
						<br/>
					-->
					<template v-if="visibleChunkIndexes.includes( chunkIndex )">
						<div class="BaseMultiSelect__optionsItem" v-for="(option) in chunk" :key="'cEntry' + option.index"
							:class="{
								'BaseMultiSelect__optionsItem--isSelected' : option.selected,
								'BaseMultiSelect__optionsItem--isHidden'   : !option.isVisible,
							}"
							v-if="option.isVisible"
							@click="clickFakeOption( $event, option )"
							>
							<span class="BaseMultiSelect__checkbox"
								:class="{'BaseMultiSelect__checkbox--isSelected' : option.selected}"
							></span>
							<span v-html="option.label"></span>
						</div>
					</template>
				</div>
				<div class="BaseMultiSelect__loadNextChunkTrigger" ref="loadNextChunkTrigger" v-if="showLoadNextChunkTrigger"><mark>Load more ...</mark></div>

				<!--
				<pre name="internalOptionsChunks">{{internalOptionsChunks}}</pre>

				<div  class="BaseMultiSelect__optionsItem" v-for="(option) in internalOptions" :key="'entry' + option.index"
					:class="{
						'BaseMultiSelect__optionsItem--isSelected' : option.selected,
						'BaseMultiSelect__optionsItem--isHidden'   : !option.isVisible,
					}"
					v-if="option.isVisible"
					@click="clickFakeOption( $event, option )"
					>
					<span class="BaseMultiSelect__checkbox"
						:class="{'BaseMultiSelect__checkbox--isSelected' : option.selected}"
					></span>
					<span v-html="option.label"></span>
				</div>
				-->
			</div>
		</div>
		<template v-if="debug">
			<br />
			<pre name="BaseMultiSelect.model">{{model}}</pre>
			<pre name="BaseMultiSelect.value">{{value}}</pre>
			<pre name="BaseMultiSelect.options">{{options}}</pre>
			<pre name="BaseMultiSelect.internalOptions">{{internalOptions}}</pre>
		</template>
	</div>
</template>

<script>
	// @ is an alias to /src
	//import DevInfos from '@/components/DevInfos.vue'
	//import { EventBus } from '@/event-bus.js'

	import debounce from 'lodash/debounce';

	export default {
		name: 'BaseMultiSelect',
		components: {},
		mixins: [],
		props: {
    		value: {
      			type: [Array, Boolean],
      			default: function(){ return [] },
    		},
    		label: {
      			type: [String, Number, Boolean],
      			default: '',
    		},
    		showFilterBar: {
      			type: [String, Boolean],
      			default: true,
    		},
    		options: {
      			type: [Array],
      			default: function(){ return [] },
    		},
    		chunkSize: {
      			type: [Number, Boolean],
      			default: false,
    		},
			disabled: {
				type: [String, Boolean],
				default: false,
			},
    		debug: {
				type: [Boolean],
      			default: false,
    		},
    	},
		data() {
			return {
				multiple                         : true,
				model                            : null,
				internalOptions                  : [],
				doWatchInternalOptions           : true,
				doUpdateInternalOptions          : true,
				filterModel                      : '',
				visibleChunkIndexes              : [],
				isVisibleLoadNextChunkTriggerElm : false,
				checkTriggerElmVisibilityInterval: undefined,
			}
		},
		watch: {
			value: {
				// watch prop value and set to model
				// this is needed to react on model changes from outside
				handler: function( to, from ) {
					//console.log('watcher value', from, to)

					this.model = to
					this.updateInternalOptions()
				},
				immediate: true,
			},
			filterModel: {
				handler: function( to, from ) {
					//console.log('watcher filterModel', from, to)

					this.filterModel = this.filterModel.trim() // no spaces allowed
					this.updateInternalOptions()
				},
				immediate: true,
			},
			options: {
				// watch prop options and set internalOptions
				// this is needed to react on options changes from outside
				handler: function( to, from ) {
					//console.log('watcher options', from, to)

					this.setInternalOptions()
				},
				immediate: true,
			},
		},
		computed: {
			elmClasses(){
				let classes = []

				if( this.multiple ) classes.push( this.$options.name + '--isMultiple')
				if( this.disabled ) classes.push( this.$options.name + '--isDisabled' )

				return classes
			},
			countFilteredOptions(){
				const results = this.internalOptions.filter( item => item.isVisible === true )

				return results.length
			},
			countSelectedOptions(){
				const results = this.internalOptions.filter( item => item.selected === true )

				return results.length
			},
			countInternalOptions(){
				return this.internalOptions.length
			},
			countChunks(){
				return this.internalOptionsChunks.length
			},
			internalOptionsChunks(){
				const chunkSize = this._.isNumber( this.chunkSize ) ? this.chunkSize : this.internalOptions.length

				return this._.chunk( this.internalOptions, chunkSize )
			},
			showLoadNextChunkTrigger(){
				return (this.visibleChunkIndexes.length < this.countChunks ) ? true : false
			},
		},
		methods: {
			emit( event ) { // emit input (values only) and change (event)
				const returnValue = this._.isArray( this.model ) ? [...this.model] : this.model

				//console.log('BaseMultiSelect emit:', event, returnValue);

				this.$emit('change', event)
				this.$emit('input', returnValue) // this changes parent v-model
			},
			clickOption( e, option, index ){ // implement later
				// handles only multiple selects
				if( !this.multiple ) return

				if( this.multiple ) return

				const model    = this.model
				const isActive = model.includes( option )

				/*
				console.group('clickOption:', option)
				console.log('       e:', e)
				console.log('  option:', option)
				console.log('   index:', index)
				console.log('   model:', model)
				console.log('isActive:', isActive)
				console.groupEnd()
				*/

				if( !isActive ){
					model.splice( index, 0, option )
				}else{
					model.splice(index, 1)
				}

				e.stopPropagation();
				e.preventDefault();
			},
			setInternalOptions(){
				const selectedValues      = this.value
				const options             = this.options
				this.doWatchInternalOptions = false
				this.internalOptions      = []

				options.forEach((item, index) => {
					const newItem = {
						index     : index, // just used as key-prop for vue's v-for-directive
						label     : item,
						value     : item,
						isVisible : true,
						selected  : (selectedValues && this._.isArray(selectedValues)) ? selectedValues.includes( item ) : false,
					}
					newItem.isVisible = this.isVisibleOption( newItem )
					this.internalOptions.push( newItem )
				})

				this.doWatchInternalOptions = true
			},
			updateInternalOptions(){
				const selectedValues      = this.value
				const internalOptions     = this.internalOptions

				this.doWatchInternalOptions = false

				/*
				console.group('updateInternalOptions()')
				console.log('selectedValues:', selectedValues)
				console.log('internalOptions:', internalOptions)
				console.groupEnd()
				*/

				internalOptions.forEach((item, index) => {
					item.isVisible = this.isVisibleOption( item )
					item.selected  = (selectedValues && this._.isArray(selectedValues)) ? selectedValues.includes( item.value ) : false
				})

				this.doWatchInternalOptions = true
			},
			isVisibleOption( option ){
				const useFilterModel = this.filterModel && this.filterModel.length
				let isVisible = true

				if( useFilterModel ){
					const testValue = option.value.toLowerCase()
					const testAgainstValue = this.filterModel.toLowerCase()
					const isMatching = testValue.includes( testAgainstValue )
					//console.log( index, testValue, testAgainstValue, isMatching )
					isVisible = isMatching
				}

				return isVisible
			},
			clickFakeOption( e, option ){
				const oldState = option.selected
				const newState = !option.selected
				const internalOptions = this.internalOptions
				const newValue = this.multiple ? [] : ''

				option.selected = newState

				internalOptions.forEach((item) => {
					if( this.multiple && item.selected ){
						newValue.push( item.value )
					}
				})

				/*
				console.group('clickFakeOption()')
				console.log('option:', option)
				console.log('option.value:', option.value)
				console.log('oldState:', oldState)
				console.log('newState:', newState)
				console.log('newValue:', newValue)
				console.groupEnd()
				*/

				// set changed model and emit
				this.model = newValue
				this.emit( e )
			},
			doRenderOptionsChunk( chunkIndex ){
				if( !this.visibleChunkIndexes.includes( chunkIndex ) ) this.visibleChunkIndexes.push( chunkIndex )
			},
			loadNextChunk( doLog = true ){
				if( doLog ){
					console.group( this.$options.name + ' • loadNextChunk()')
				}

				const chunks = this.internalOptionsChunks

				let done = false
				this._.forEach( chunks, (item, index) => {
					if( !done && !this.visibleChunkIndexes.includes(index) ){
						this.visibleChunkIndexes.push( index )
						done = true

						if( doLog ) console.log('chunk index:', index)
					}
				})

				if( doLog ){
					console.groupEnd()
				}
			},
		},
		created(){},
		mounted(){
			const optionsListElm = this.$refs.optionsList
			const loadNextChunkTriggerElm = this.$refs.loadNextChunkTrigger
			//const chunkElms = document.querySelectorAll('.BaseMultiSelect__optionsChunk')

			console.log('optionsListElm:', optionsListElm)
			console.log('loadNextChunkTriggerElm:', loadNextChunkTriggerElm)

			// super simple but powerfull example taken from
			// https://www.cssscript.com/is-in-viewport-intersectionobserver/
			function inViewport(elem, callback, options = {}) {
				return new IntersectionObserver(entries => {
					entries.forEach(entry => callback(entry))
				}, options).observe( elem )
			}

			inViewport(loadNextChunkTriggerElm, entry => {
				const isIntersecting = entry.isIntersecting
				this.isVisibleLoadNextChunkTriggerElm = isIntersecting
			}, {
				root: optionsListElm
			})

			this.checkTriggerElmVisibilityInterval = setInterval(() => {
				//console.log('checkTriggerElmVisibilityInterval')
				if( this.isVisibleLoadNextChunkTriggerElm ) this.loadNextChunk()
			}, 250)

			/*
				chunkElms.forEach((chunkElm, chunkIndex)=>{
					inViewport(chunkElm, entry => {
						const isIntersecting = entry.isIntersecting
						//console.log('chunkIndex:', chunkIndex, isIntersecting)
						//if( isIntersecting ) this.visibleChunkIndexes.push( chunkIndex )
					}, {
						root: optionsListElm // document.querySelector('.scroll')
					})
				})

				inViewport('.target', element => {
					// returns true or false
				}, {
					root: optionsListElm // document.querySelector('.scroll')
				})
			*/
		},
		beforeDestroy(){
			clearInterval( this.checkTriggerElmVisibilityInterval )
		}
	}
</script>

<style lang="less">
	@import (reference) "./../v1/vars.less";
	@import (reference) "./../v1/mixins.less";

	XXX.BaseMultiSelect {  // debug styling
		//margin: 1em 0;

		&__optionsChunk {
			border: 1px solid lighten( red, 35 );
			padding: 0.5em;
			margin-top: -1px;
		}
		&__optionsChunkLoadButton {
			color: blue;
		}
	}

	.BaseMultiSelect {
		position: relative;
		width: 100%;
		user-select: none;

		&__inner {
			position: relative;
			height: @selectElm[height];
			background-color: @selectElm[backgroundColor];
			border-radius: @selectElm[borderRadius];
			border: @selectElm[border];
			color: inherit;
			cursor: pointer;
		}
		&__select {
			max-width: 100%;
			width: 100%;
			height: 100%;
			text-indent: @selectElm[paddingLeft];
			cursor: inherit;

			border: none;
			outline: none;
			background-color: transparent;
			-webkit-appearance: none;
    		-moz-appearance: none;
    		appearance: none;
			font: inherit;
			color: inherit;
			padding: 0;
			margin: 0;
			padding-right: 2em; // prevent triangle-icon overlapping
		}
		&__select--isUnset { // lighten "Choose ..."
			opacity: 0.5;
		}
		&:hover:not(.BaseMultiSelect--isDisabled) &__select--isUnset {
			opacity: 0.65;
		}
		&__option {
			padding-left: @selectElm[paddingLeft];
			background-color: transparent;
			//-webkit-appearance: none;
			//-moz-appearance: none;
			//appearance: none;
		}
		&__icon {
			//background-color: fade(red, 60);

			position: absolute;
			top: 0; right: 0;
			height: @selectElm[height];
			width: 2em;
			opacity: 0.6;
			color: currentColor;

			display: flex;
			justify-content: center;
			align-items: center;
			pointer-events: none;

			&::before {
				font-size: 0.65em;
				line-height: 1em;
				content: "▼";
			}
		}
	}
	.BaseMultiSelect:hover:not(.BaseMultiSelect--isDisabled) {
		.BaseMultiSelect__inner { border: @selectElm[hoverBorder]; }
		.BaseMultiSelect__icon { opacity: 1; }
	}
	.BaseMultiSelect--isDisabled {
		opacity: 0.85;

		.BaseMultiSelect__inner {
			border-style: dashed;
			background-color: fade( black, 3 );
			cursor: default;
		}
	}
	.BaseMultiSelect--isMultiple {
		.BaseMultiSelect__inner {
			height: auto;
			padding: 0;
			/*
			min-height: @selectElm[multipleMinHeight];
			max-height: @selectElm[multipleMaxHeight];
			overflow: hidden;
			overflow-y: auto;
			*/
		}
		.BaseMultiSelect__select { // hidden and replaced by .BaseMultiSelect__optionsList
			display: none;
		}
		.BaseMultiSelect__filterBar {
			position: relative;
			display: flex;
			justify-content: flex-end;
			align-items: center;
			padding: 0 0.25em;
			height: @selectElm[height];
			border-bottom: @selectElm[border];
			background-color: @selectElm[filterBarBackgroundColor];

			&CountSelected,
			&Input,
			&CountResults {
				font-size: 0.85em;
			}
			&Input {
				max-width: 100%;
				width: 200px;
				border-radius: @selectElm[height];
				//width: 80%;
				height: @selectElm[height]*0.75;
				text-indent: @selectElm[paddingLeft];
				//text-indent: var(--paddingLeft);
				//cursor: pointer;

				transition: all 0.2s ease;
				border: none;
				outline: none;
				background-color: white;
				-webkit-appearance: none;
				-moz-appearance: none;
				appearance: none;
				font-family: inherit;
				color: inherit;
				padding: 0;
				margin: 0;

				&::placeholder {
					opacity: 0.35;
					color: currentColor;
				}
				&:focus {
					outline: none;
					opacity: 1;
					width: 100%;

					&::placeholder {
						//opacity: 0;
					}
				}
			}
			&CountResults {
				//background-color: fade( red, 20 );
				background-color: white;

				position: absolute;
				top: 0.4em; bottom: 0.4em; right: 0.25em;
				display: flex;
				align-items: center;
				padding: 0 0.75em;
				color: fade( black, 50 );
				border-top-right-radius: 1em;
				border-bottom-right-radius: 1em;
			}
			&CountSelected {
				display: none !important;
				//background-color: fade( red, 20 );

				display: flex;
				justify-content: center;
				border-radius: 1em;
				width: 2em;
				background-color: @selectElm[activeBackgroundColor];
				color: @selectElm[activeForegroundColor];
			}
		}
		.BaseMultiSelect__optionsList {
			min-height: @selectElm[multipleMinHeight];
			max-height: @selectElm[multipleMaxHeight];
			overflow: hidden;
			overflow-y: auto;
		}
		/*
		.BaseMultiSelect__option { // hidden and replaced by .BaseMultiSelect__optionsItem
			height: @selectElm[height];
			display: flex;
			align-items: center;

			&:not(:first-child){ border-top: @selectElm[border]; }
		}
		.BaseMultiSelect__option--label {
			display: none;
		}
		*/

		.BaseMultiSelect__optionsItem {
			height: @selectElm[height];
			padding-left: @selectElm[paddingLeft];
			display: flex;
			align-items: center;
			white-space: nowrap;
			overflow: hidden;
			//text-overflow: ellipsis;
			transition: all 0.1s ease;

			//&:not(:first-child){ border-top: @selectElm[border]; }
			border-top: none !important; // overwrite old BaseMultiSelect styles, remove later
			border-bottom: @selectElm[border];
			//&::before { content: "☑" }
			&::before {
				//content: "☐";
				//padding-right: 0.25em;
				//font-size: 1.2em;
			}
		}
		.BaseMultiSelect__optionsItem--isSelected {
			background-color: @selectElm[activeBackgroundColor];
			color: @selectElm[activeForegroundColor];
		}
		.BaseMultiSelect__optionsItem--isHidden {
			//color: red;
			height: 0px;
			margin-top: -1px;
			border-color: transparent !important;
			background-color: transparent !important;
		}
		.BaseMultiSelect__checkbox {
			.checkboxMixin();

			margin-right: 0.5em;
		}

		.BaseMultiSelect__loadNextChunkTrigger {
			& > * { visibility: hidden; }
		}
	}
	.BaseMultiSelect--isMultiple.BaseMultiSelect--isDisabled {
		pointer-events: none;
		user-select: none; // really needed?

		.BaseMultiSelect__optionsItem {
			opacity: 0.5;
		}
		.BaseMultiSelect__filterBar {
			opacity: 0.5;
		}
	}



</style>
